import { PositionModel } from '@cas/types/assets';
import { casOpsApi, CollectionFilteredModel, QueryTag, TagList } from '../api';
import { ModelWithId, NameIdBasicModel, SafetyAlertParams, SafetyAlertType } from './commonModels';
import { getUrlParams } from './helpers';
import { ObjectType } from '@dmc/ui';

export interface SafetyAlertBasicModel extends ModelWithId {
  did: string; // Device ID of the PPU
  as?: NameIdBasicModel; // Link to asset, if appropriate
  typ: SafetyAlertType;
  ts: string; // Alert origination time
  ack: boolean; // Acknowledged
  ackAt?: string; // Alert acknowledgement time
  ackBy?: NameIdBasicModel; // Alert acknowledgement user
}

export enum EmailNotificationSendStatus {
  Pending = 'Pending',
  Sent = 'Sent',
  Error = 'Error',
}

export interface SafetyAlertNotificationModel extends ModelWithId {
  n: string; // display name or email
  st: EmailNotificationSendStatus;
  ts: string | null; // timestamp. Only set when Status is Sent.
}

export interface SafetyAlertNearbyAssetModel {
  as: NameIdBasicModel; // nearby asset name and ID
  obj: ObjectType;
  posG: PositionModel;
}

export interface SafetyAlertModel extends SafetyAlertBasicModel {
  not?: SafetyAlertNotificationModel[];
  posA: PositionModel; // Alert position
  posG: PositionModel | null; // Last known good position of the asset that triggered the alert.
  nby: SafetyAlertNearbyAssetModel[]; // Recently reported assets near the event vicinity.
}

export interface SafetyAlertStatusNotificationModel {
  md: number;
  ds: number;
}

export const personnelSafetyApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    listSafetyAlerts: build.query<
      CollectionFilteredModel<SafetyAlertBasicModel>,
      SafetyAlertParams
    >({
      query: arg => ({
        url: 'api/safetyAlerts',
        params: getUrlParams(arg),
      }),
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.SafetyAlert, id: TagList }] : [],
    }),
    getSafetyAlert: build.query<SafetyAlertModel, ModelWithId>({
      query: ({ id }) => `api/safetyAlerts/${id}`,
      providesTags: (result, error, { id }) =>
        result ? [{ type: QueryTag.SafetyAlert, id: id }] : [],
    }),
    liveSafetyAlertCounts: build.query<SafetyAlertStatusNotificationModel, void>({
      queryFn: () => ({ data: { md: 0, ds: 0 } }),
      async onCacheEntryAdded(
        arg,
        { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const es = new EventSource('api/safetyalerts/notifications');

        try {
          await cacheDataLoaded;

          const onAlertMessage = (e: MessageEvent) => {
            try {
              const notification = JSON.parse(e.data) as SafetyAlertStatusNotificationModel;
              if (!notification) return;
              updateCachedData(draft => {
                draft.ds = notification.ds;
                draft.md = notification.md;
              });
              dispatch(
                casOpsApi.util.invalidateTags([{ type: QueryTag.SafetyAlert, id: TagList }]),
              );
            } catch (err) {
              console.log('Safety Alerts EventSource Error', err);
            }
          };

          es.addEventListener('alt', onAlertMessage);

          await cacheEntryRemoved;
        } finally {
          es.close();
        }
      },
    }),
    acknowledgeSafetyAlert: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/safetyAlerts/${id}/acknowledge`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.SafetyAlert, id: TagList },
              { type: QueryTag.SafetyAlert, id: id },
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListSafetyAlertsQuery,
  useGetSafetyAlertQuery,
  useLiveSafetyAlertCountsQuery,
  useAcknowledgeSafetyAlertMutation,
} = personnelSafetyApi;
